import React, { memo, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { Tabs, Icon } from '@Sage/hoi-poi-ui';
import { Route, Routes } from 'react-router-dom';
import { RouteOnMount, ensureRoute } from 'utils/routes';
import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';
import SettingsLayout from '../components/SettingsLayout';
import AutomationsList from './AutomationsList';
import AutomationsLog from './AutomationsLog';
import AutomationBuilder from './components/AutomationBuilder';
import AutomationsTemplates from './AutomationsTemplates';

import './styles.scss';

const AutomationSettings = memo(() => {
    const modalRef = useRef(null);
    const [activeTab, setActiveTab] = useState('automations-list');

    const onModalRef = useCallback((ref) => {
        modalRef.current = ref;
    }, []);

    const openBuilder = useCallback((...props) => {
        modalRef.current?.open(...props);
    }, []);

    const tabs = useMemo(
        () => [
            {
                key: 'automations-list',
                title: getLiteral('label_automations'),
            },
            {
                key: 'automations-log',
                title: getLiteral('label_automation_history'),
            },
            {
                key: 'automations-templates',
                title: getLiteral('label_automations_templates'),
            },
        ],
        [],
    );

    useEffect(() => {
        logEvent({
            event: 'settings',
            submodule: 'automations',
            functionality: 'listView',
        });
    }, []);

    const goToTemplates = useCallback(() => {
        ensureRoute('/settings/automations/templates');
    }, []);

    const learnMoreItems = useMemo(() => {
        return [
            {
                title: getLiteral('action_automations_knowledge_base'),
                icon: <Icon name="document" />,
                onClick: () => {
                    window.open(getLiteral('label_automations_knowledge_base_link'), '_blank');
                },
            },
        ];
    }, []);

    return (
        <SettingsLayout
            className="fm-automation-settings"
            title={getLiteral('label_automations')}
            description={getLiteral('label_automations_desc')}
            withoutDivider
            learnMoreItems={learnMoreItems}
        >
            <Tabs
                onChange={(key) => {
                    setActiveTab(key);
                    switch (key) {
                        case 'automations-list':
                            ensureRoute('/settings/automations');
                            break;
                        case 'automations-log':
                            ensureRoute('/settings/automations/logs');
                            break;
                        case 'automations-templates':
                            ensureRoute('/settings/automations/templates');
                            break;
                        default:
                            break;
                    }
                }}
                activeKey={activeTab}
                tabs={tabs}
            />
            {activeTab === 'automations-list' && (
                <AutomationsList openBuilder={openBuilder} goToTemplates={goToTemplates} />
            )}
            {activeTab === 'automations-log' && (
                <AutomationsLog openBuilder={openBuilder} goToTemplates={goToTemplates} />
            )}
            {activeTab === 'automations-templates' && (
                <AutomationsTemplates openBuilder={openBuilder} />
            )}
            <AutomationBuilder onRef={onModalRef} />
            <Routes>
                <Route
                    path="/"
                    element={
                        <RouteOnMount
                            onMount={() => {
                                setActiveTab('automations-list');
                            }}
                        />
                    }
                />
                <Route
                    path="/logs"
                    element={
                        <RouteOnMount
                            onMount={() => {
                                setActiveTab('automations-log');
                            }}
                        />
                    }
                />
                <Route
                    path="/templates"
                    element={
                        <RouteOnMount
                            onMount={() => {
                                setActiveTab('automations-templates');
                            }}
                        />
                    }
                />
                <Route
                    path="/:id"
                    element={
                        <RouteOnMount
                            onMount={(params) => {
                                if (!params.id) return;
                                modalRef.current?.open(params.id);
                            }}
                        />
                    }
                />
            </Routes>
        </SettingsLayout>
    );
});

export default AutomationSettings;
